import { useEffect, useState, type ReactNode } from "react";
import { Button } from "@client/components/Button";
import { Modal } from "@client/components/Modal";

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  /** What will actually happen, in plain English. Not "Are you sure?". */
  body: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  tone?: "primary" | "danger";
  /**
   * When given, the confirm button stays disabled until this exact text is
   * typed. Reserved for the irreversible — a hard delete — because a
   * confirmation everyone types on reflex protects nobody.
   */
  confirmPhrase?: string;
  busy?: boolean;
  error?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * The ZNMS `ConfirmDialog` generalised. That one hard-coded a reason field and a
 * TOTP code because every action behind it was a licence revocation; here the
 * dangerous actions are archiving and unsharing, so the typed phrase is opt-in
 * and the rest is one sentence saying what happens.
 */
export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  cancelLabel = "Cancel",
  tone = "primary",
  confirmPhrase,
  busy = false,
  error,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const [typed, setTyped] = useState("");

  useEffect(() => {
    if (open) setTyped("");
  }, [open]);

  const canConfirm = !busy && (!confirmPhrase || typed.trim() === confirmPhrase.trim());

  return (
    <Modal
      open={open}
      title={title}
      onClose={busy ? () => undefined : onCancel}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </Button>
          <Button
            variant={tone === "danger" ? "destructive" : "primary"}
            onClick={onConfirm}
            disabled={!canConfirm}
          >
            {busy ? "Working…" : confirmLabel}
          </Button>
        </>
      }
    >
      <div className="text-sm text-ink-soft">{body}</div>

      {confirmPhrase && (
        <label className="flex flex-col gap-1.5 text-xs font-semibold text-ink">
          Type “{confirmPhrase}” to confirm
          <input
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            autoComplete="off"
            className="rounded-md border border-line bg-chip-bg px-3 py-2 text-sm font-normal text-ink"
          />
        </label>
      )}

      {error && (
        <p role="alert" className="text-xs font-semibold" style={{ color: "var(--chart-crit)" }}>
          {error}
        </p>
      )}
    </Modal>
  );
}
